/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {Component, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {HttpClient} from '@angular/common/http';
import {
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  createPart,
  themeAlpine,
} from 'ag-grid-community';
import {LocalizationHelper} from '../../../shared/utils/localization-helper';
import {ModalService, ToastService} from '@siemens/ix-angular';
import {ModalAddDocumentComponent} from './modal-add-document/modal-add-document.component';
import {TranslateService} from '@ngx-translate/core';
import {Subscription, firstValueFrom} from 'rxjs';
import {DppService} from '../../../../services/dpp.service';
import {HandoverDocument} from '../../../../models/dpp/handover-document';
import {Submodel} from '@product-passport/dpp/model';
import {getIxTheme} from '@siemens/ix-aggrid';

@Component({
  selector: 'app-handover-documentation',
  templateUrl: './handover-documentation.component.html',
  styleUrl: './handover-documentation.component.scss',
  standalone: false,
})
export class HandoverDocumentationComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly http = inject(HttpClient);
  protected readonly localizationHelper = inject(LocalizationHelper);
  private readonly modalService = inject(ModalService);
  private readonly translate = inject(TranslateService);
  private readonly dppService = inject(DppService);
  private readonly toastService = inject(ToastService);

  passportId: string | null = null;
  submodelId: string | null = null;
  @Input() passportName?: string;
  loading = true;
  columnDefs: ColDef[] = [];
  handoverDocuments: HandoverDocument[] = [];
  selectedDocuments: HandoverDocument[] = [];
  gridApi: GridApi | null = null;
  gridOptions: GridOptions = {
    theme: getIxTheme({createPart, themeAlpine}),
    rowSelection: 'multiple',
    suppressRowClickSelection: true,
    suppressNoRowsOverlay: true,
  };

  defaultColDef: ColDef = {
    sortable: false,
    filter: false,
    resizable: true,
  };

  private langChangeSubscription?: Subscription;

  ngOnInit(): void {
    this.passportId = this.route.snapshot.paramMap.get('aasId');
    this.submodelId = this.route.snapshot.paramMap.get('submodelId');
    if (this.passportId && this.passportName === undefined) {
      this.loadPassportData(this.passportId);
    }
    this.initializeColumnDefs();
    void this.loadData();

    this.langChangeSubscription = this.translate.onLangChange.subscribe(() => {
      if (this.gridApi) {
        this.gridApi.refreshHeader();
        this.gridApi.refreshCells({force: true});
      }
    });
  }

  ngOnDestroy(): void {
    this.langChangeSubscription?.unsubscribe();
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
  }

  onSelectionChanged(): void {
    if (this.gridApi) {
      this.selectedDocuments = this.gridApi.getSelectedRows();
    }
  }

  private initializeColumnDefs(): void {
    this.columnDefs = [
      {
        headerValueGetter: () =>
          this.translate.instant('dpp.handover_documentation.columns.title'),
        valueGetter: (params) =>
          this.localizationHelper.getLocalizedText(
            params.data?.title,
            undefined,
            params.data?.language,
          ),
        flex: 2,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        minWidth: 150,
      },
      {
        headerValueGetter: () =>
          this.translate.instant('dpp.handover_documentation.columns.category'),
        valueGetter: (params) =>
          this.translate.instant(
            'dpp.handover_documentation.modal.categories.' +
              params.data?.category[0]?.text,
          ),

        flex: 1,
        minWidth: 150,
      },
      {
        headerValueGetter: () =>
          this.translate.instant('dpp.handover_documentation.columns.language'),
        field: 'language',
        flex: 1,
        minWidth: 150,
      },
      {
        headerValueGetter: () =>
          this.translate.instant('dpp.handover_documentation.columns.type'),
        field: 'type',
        flex: 1,
        minWidth: 150,
      },
      {
        headerValueGetter: () =>
          this.translate.instant('dpp.handover_documentation.columns.date'),
        field: 'date',
        valueFormatter: (params) => {
          const raw: string = params.value;
          if (!raw) {
            return '';
          }
          const d = new Date(raw);
          return isNaN(d.getTime())
            ? raw
            : d.toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'short',
                day: '2-digit',
              });
        },
        flex: 1,
        minWidth: 150,
      },
      {
        headerValueGetter: () =>
          this.translate.instant('dpp.handover_documentation.columns.version'),
        field: 'version',
        flex: 1,
        minWidth: 150,
      },
    ];
  }

  private async loadData(): Promise<void> {
    if (!this.submodelId) {
      if (this.passportId) {
        await this.createHandoverDocumentationSubmodel();
      } else {
        this.loading = false;
      }
      return;
    }
    try {
      this.handoverDocuments = await this.dppService.getHandoverDocuments(
        this.submodelId,
      );
    } catch {
      await this.toastService.show({
        message: this.translate.instant(
          'dpp.handover_documentation.load_error',
        ),
        type: 'error',
      });
    } finally {
      this.loading = false;
    }
  }

  private async createHandoverDocumentationSubmodel(): Promise<void> {
    const passportId = this.passportId;
    if (!passportId) {
      this.loading = false;
      return;
    }
    try {
      const template = await firstValueFrom(
        this.http.get<Submodel>(
          '/assets/dpp/handover_documentation_schema.json',
        ),
      );
      const created = await this.dppService.createSubmodel(
        passportId,
        template,
      );
      this.submodelId = created.id;
      await this.toastService.show({
        message: this.translate.instant(
          'dpp.generic_submodel_editor.save_success',
        ),
        type: 'success',
      });
      this.router.navigate(
        ['/app/dpp/view-passport', this.passportId, 'submodel', created.id],
        {replaceUrl: true},
      );
    } catch {
      await this.toastService.show({
        message: this.translate.instant(
          'dpp.handover_documentation.create_error',
        ),
        type: 'error',
      });
      this.navigateToViewPassport();
    } finally {
      this.loading = false;
    }
  }

  private async loadPassportData(passportId: string): Promise<void> {
    try {
      const shellData = await this.dppService.getDppShell(passportId);
      this.passportName = shellData.displayName?.[0]?.text || passportId;
    } catch {
      this.passportName = passportId;
      await this.toastService.show({
        message: this.translate.instant(
          'dpp.passport_detail_view.load_error_message',
        ),
        type: 'error',
      });
    }
  }

  navigateToPassports(): void {
    this.router.navigate(['/app/dpp/list-passports']);
  }

  navigateToViewPassport(): void {
    if (this.passportId) {
      this.router.navigate(['/app/dpp/view-passport', this.passportId]);
    }
  }

  private getSelectedDocumentIds(): string[] {
    return this.selectedDocuments
      .map((doc) => doc.id)
      .filter((id) => id?.trim()) as string[];
  }

  async openAddDocumentModal(): Promise<void> {
    const modal = await this.modalService.open({
      content: ModalAddDocumentComponent,
      centered: true,
      size: '720',
      data: {submodelId: this.submodelId},
    });
    await new Promise<void>((resolve) => {
      modal.onClose.once(async (result) => {
        if (result?.success) {
          await this.loadData();
        }
        resolve();
      });
    });
  }

  async downloadSelectedDocuments(): Promise<void> {
    if (!this.submodelId || this.selectedDocuments.length === 0) {
      return;
    }

    const documentIds = this.getSelectedDocumentIds();

    if (documentIds.length === 0) {
      return;
    }

    try {
      const files = await this.dppService.getSubmodelAttachments(
        this.submodelId,
        documentIds,
      );
      for (const file of files) {
        const url = URL.createObjectURL(file.blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.filename || '';
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch {
      await this.toastService.show({
        message: this.translate.instant(
          'dpp.handover_documentation.download_error',
        ),
        type: 'error',
      });
    }
  }

  async deleteSelectedDocuments(): Promise<void> {
    if (!this.submodelId || this.selectedDocuments.length === 0) {
      return;
    }

    const documentIds = this.getSelectedDocumentIds();

    if (documentIds.length === 0) {
      return;
    }

    try {
      await this.dppService.deleteSubmodelAttachments(
        this.submodelId,
        documentIds,
      );
      await this.loadData();
      this.selectedDocuments = [];
      await this.toastService.show({
        message: this.translate.instant(
          'dpp.handover_documentation.delete_success',
        ),
        type: 'success',
      });
    } catch {
      await this.toastService.show({
        message: this.translate.instant(
          'dpp.handover_documentation.delete_error',
        ),
        type: 'error',
      });
    }
  }
}
